<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import { useDisplay } from 'vuetify';
import useLocale from '../../composables/useLocale';
import HighlightColorPickerButton from './HighlightColorPickerButton.vue';
import type { Highlight, Underline } from '@ilteducation/annotations-types';
import useHighlightsStore from '../../stores/highlights';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faUnderline } from '@fortawesome/pro-regular-svg-icons';
library.add(faUnderline);

type Emits = {
  (e: 'save', color: string, type: Highlight['type'] | Underline['type'], comment?: string): void;
  (e: 'remove'): void;
  (e: 'cancel'): void;
};
const emits = defineEmits<Emits>();

type Props = {
  text?: string;
  color?: string;
  type?: Highlight['type'] | Underline['type'];
  comment?: string;
  state: 'edit' | 'new';
  isSaving?: boolean;
  isRemoving?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  color: 'pink',
  type: 'highlight',
});
const { color, type, comment } = toRefs(props);

const { t } = useLocale();
const { smAndDown } = useDisplay();
const { highlightColors } = useHighlightsStore();

// Filter out 'red' from the display (it's only for underlines)
const displayColors = highlightColors.filter((c) => c.name !== 'red');

const newColor = ref(props.color);
const newType = ref<Highlight['type'] | Underline['type']>(props.type);
const newComment = ref<string | undefined>(props.comment);
const isValid = ref(false);
const form = ref();

watch(
  [color, type, comment],
  ([colorValue, typeValue, commentValue]) => {
    newColor.value = colorValue || 'pink';
    newType.value = typeValue || 'highlight';
    newComment.value = commentValue;
  },
  { immediate: true },
);

const selectColor = (color: string) => {
  newColor.value = color;
  newType.value = 'highlight';
};

const selectUnderline = () => {
  newColor.value = 'red';
  newType.value = 'underline';
};

const isUnderlineActive = computed(() => newType.value === 'underline');

const submit = () => {
  // No validation needed for highlights (color always selected, comment optional)
  // But we keep the form wrapper for consistency with BookmarkForm
  emits('save', newColor.value, newType.value, newComment.value);
};

const handleCancel = () => {
  // Reset form to original values
  newColor.value = color.value || 'pink';
  newType.value = type.value || 'highlight';
  newComment.value = comment.value;
  emits('cancel');
};
</script>

<template>
  <v-form ref="form" v-model="isValid" @submit.prevent="submit">
    <v-card-text>
      <v-row dense>
        <v-col>
          <div class="border-md border-on-surface-light rounded pa-3 w-100">
            {{ text }}
          </div>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col
          role="group"
          :aria-label="t('book_player.label_highlight_toolbar_title')"
          class="d-flex align-center ga-1"
        >
          <HighlightColorPickerButton
            v-for="color of displayColors"
            :key="`color-${color.light}`"
            :title="color.name"
            :color="color.light"
            :active="newType === 'highlight' && newColor === color.name"
            @click="selectColor(color.name)"
          />
          <v-btn
            variant="text"
            :ripple="false"
            icon
            color="on-primary"
            :class="{ 'underline-is-active': isUnderlineActive }"
            :aria-label="t('book_player.label_annotation_underline')"
            :aria-pressed="isUnderlineActive"
            :title="t('book_player.label_annotation_underline')"
            @click="selectUnderline"
          >
            <div class="underline-ball" :class="{ active: isUnderlineActive }">
              <FontAwesomeIcon icon="far fa-underline" size="sm" />
            </div>
          </v-btn>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col>
          <v-textarea
            v-model.trim="newComment"
            variant="filled"
            :hide-details="'auto'"
            :label="t('highlight_form_comment_optional')"
            :aria-label="t('highlight_form_comment_optional')"
          ></v-textarea>
        </v-col>
      </v-row>
      <slot name="highlightMessaging" :highlightedText="text" :state="state"></slot>
    </v-card-text>
    <v-card-actions class="d-flex justify-space-between mb-2">
      <v-btn
        v-if="state === 'edit'"
        :text="t('button_delete')"
        :loading="isRemoving"
        color="error"
        variant="flat"
        @click="emits('remove')"
      />
      <v-spacer></v-spacer>
      <v-btn v-if="!smAndDown" :text="t('button_cancel')" color="secondary" variant="flat" @click="handleCancel" />
      <v-btn :text="t('button_save')" :loading="isSaving" color="primary" variant="flat" type="submit" />
    </v-card-actions>
  </v-form>
</template>

<style scoped lang="scss">
.underline-is-active {
  overflow: visible !important;
}

.underline-ball {
  width: 30px;
  height: 30px;
  background-color: rgb(var(--v-theme-error));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition:
    outline 0.12s ease,
    transform 0.12s ease;

  &:hover:not(.active) {
    transform: scale(1.1);
  }

  &.active {
    outline: 3px solid var(--highlight-ring-color, rgb(var(--v-theme-on-surface)));
    outline-offset: 2px;
  }
}
</style>
